import { Injectable, signal } from '@angular/core';

import { PlanLimitKind } from '../models';

/** A refusal the server made because of the school's plan. */
export interface PlanNotice {
  code: string;
  /** Already in the reader's language — the server translated it. */
  message: string;
  /** For a limit: what it counts, and the numbers behind the sentence. */
  kind?: PlanLimitKind;
  used?: number;
  limit?: number;
  adding?: number;
  plan?: string;
}

/**
 * The plan refusals worth stopping somebody for. Each means the school has
 * reached the edge of what it bought, and the way past it is a bigger plan —
 * not a mistake in what they typed. Signing in with a switched-off role is
 * left out: there is no app around that refusal yet, and the sign-in page says
 * it already.
 */
const PLAN_CODES = new Set([
  'plan.limit.students',
  'plan.limit.teachers',
  'plan.limit.staff',
  'plan.limit.branches',
  'plan.limit.branch_admins',
  'plan.limit.principals',
  'plan.limit.accountants',
  'plan.limit.front_office',
  'plan.limit.exam_coordinators',
  'plan.attendance_history',
  'plan.not_in_plan',
  'plan.role_not_included',
  'plan.role_off_privileges'
]);

/**
 * Holds the plan refusal on screen, if any. The interceptor fills it from
 * whichever request was refused; the layout draws the popup from it. The
 * screen that made the request still gets the error and shows it inline as
 * well — the popup adds the way out, it does not replace the message.
 */
@Injectable({ providedIn: 'root' })
export class PlanNoticeService {
  private readonly _notice = signal<PlanNotice | null>(null);
  readonly notice = this._notice.asReadonly();

  /** Raise the popup if `detail` is a plan refusal. Returns whether it was. */
  offer(detail: unknown): boolean {
    if (!detail || typeof detail !== 'object') {
      return false;
    }
    const d = detail as Record<string, unknown>;
    const code = typeof d['code'] === 'string' ? d['code'] : '';
    if (!PLAN_CODES.has(code)) {
      return false;
    }
    const num = (v: unknown) => (typeof v === 'number' ? v : undefined);
    const kind = code.startsWith('plan.limit.') ? (code.slice('plan.limit.'.length) as PlanNotice['kind']) : undefined;
    this._notice.set({
      code,
      message: typeof d['message'] === 'string' ? d['message'] : '',
      kind,
      used: num(d['used']),
      limit: num(d['limit']),
      adding: num(d['adding']),
      plan: typeof d['plan'] === 'string' ? d['plan'] : undefined
    });
    return true;
  }

  dismiss(): void {
    this._notice.set(null);
  }
}
